import { Shield, Lock, Mail, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';

const Security = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-capledger-teal hover:opacity-80 transition-opacity"
          >
            CapLedger
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">CapLedger Security Overview</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Effective Date:</strong> October 2025
              </div>
              <div>
                <strong>Entity:</strong> Spesvest (Pty) Ltd (Registration No. 2016/197925/07)
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Address:</strong> Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
              Africa, 7600
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Email:</strong>{' '}
              <a
                href="mailto:security@capledger.co.za"
                className="text-capledger-teal hover:underline"
              >
                security@capledger.co.za
              </a>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. Security Philosophy</h2>
              <p className="text-muted-foreground leading-relaxed">
                At CapLedger, safeguarding sensitive company and shareholder information is our top
                priority. We design every system layer — from authentication to database storage —
                to uphold confidentiality, integrity, and availability.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our approach combines modern encryption, continuous monitoring, and governance-based
                controls that align with international standards such as ISO 27001, SOC 2, and NIST
                800-53 principles.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Encryption</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong>In transit:</strong> All network traffic uses HTTPS/TLS 1.3.
                </li>
                <li>
                  <strong>At rest:</strong> Data is encrypted with AES-256 encryption.
                </li>
                <li>
                  <strong>Secrets:</strong> Application and API credentials are stored in secure,
                  access-controlled vaults.
                </li>
                <li>
                  <strong>Backups:</strong> Encrypted and geographically redundant, retained
                  according to strict retention policies.
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                3. Authentication & Access Control
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Passwordless login via secure, expiring magic links.</li>
                <li>
                  Role-based access control (RBAC) and company-scoped permissions to isolate
                  tenants.
                </li>
                <li>Optional multi-factor authentication (MFA) for administrative accounts.</li>
                <li>Automatic session expiry and device revocation for inactive users.</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Administrative access to production systems is limited to authorised personnel under
                the principle of least privilege.
              </p>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                4. Infrastructure & Monitoring
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  Hosted in secure regional data centres in South Africa and the EU, with physical
                  access controls and 24/7 monitoring.
                </li>
                <li>
                  Continuous infrastructure health checks and uptime monitoring via BetterStack.
                </li>
                <li>Centralised log collection and anomaly detection to flag unusual activity.</li>
                <li>DDoS protection and rate limiting on all public endpoints.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">5. Application Security</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  All code undergoes static analysis, dependency scanning, and peer review prior to
                  deployment.
                </li>
                <li>Continuous vulnerability scanning and patch management.</li>
                <li>Secure API architecture with strict schema validation.</li>
                <li>Regular third-party penetration tests and remediation tracking.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                6. Audit Trails & Compliance
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Every material user action (e.g., share issuance, valuation update, company
                creation) is immutably logged with:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Timestamp</li>
                <li>User ID</li>
                <li>Company ID</li>
                <li>Action type</li>
                <li>IP and device metadata</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Audit records are stored securely and retained according to compliance and
                evidentiary requirements under POPIA and GDPR.
              </p>
            </section>

            {/* Section 7 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">7. Incident Response</h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  Dedicated security incident response plan with immediate triage and containment
                  protocols.
                </li>
                <li>
                  Notification to affected users and regulators, where required, without undue
                  delay.
                </li>
                <li>Post-incident review to strengthen future defences.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                8. Employee & Vendor Security
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  Employees receive ongoing training in cybersecurity and data-protection best
                  practices.
                </li>
                <li>Background checks are conducted for roles with sensitive access.</li>
                <li>
                  Vendors and subprocessors are assessed for compliance with POPIA/GDPR and ISO
                  27001 controls.
                </li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                9. Vulnerability Disclosure
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                We encourage responsible disclosure. If you discover a vulnerability, contact{' '}
                <a
                  href="mailto:security@capledger.co.za"
                  className="text-capledger-teal hover:underline"
                >
                  security@capledger.co.za
                </a>{' '}
                with details. We investigate all reports promptly and appreciate contributions to
                platform safety.
              </p>
            </section>

            {/* Section 10 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Future Commitments</h2>
              <p className="text-muted-foreground leading-relaxed">
                Spesvest is working toward formal ISO 27001 certification and SOC 2 Type II
                attestation. We will publish updates on progress in our forthcoming Trust Center.
              </p>
            </section>

            {/* Contact Section */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">Contact</h2>
              <div className="bg-muted p-6 rounded-lg space-y-2">
                <a
                  href="mailto:security@capledger.co.za"
                  className="flex items-center gap-2 text-capledger-teal hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  security@capledger.co.za
                </a>
                <p className="text-sm text-muted-foreground">
                  Spesvest (Pty) Ltd, Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
                  Africa, 7600
                </p>
              </div>
            </section>
          </div>

          {/* Security Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <Lock className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">TLS 1.3 & AES-256</p>
                <p className="text-xs text-muted-foreground">End-to-end encryption</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <Shield className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">ISO 27001 Aligned</p>
                <p className="text-xs text-muted-foreground">Security standards</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <Eye className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">24/7 Monitoring</p>
                <p className="text-xs text-muted-foreground">Continuous surveillance</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <AlertTriangle className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">Incident Response</p>
                <p className="text-xs text-muted-foreground">Rapid containment</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <CheckCircle className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">Audit Trails</p>
                <p className="text-xs text-muted-foreground">Immutable logging</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <Lock className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">RBAC & MFA</p>
                <p className="text-xs text-muted-foreground">Access control</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Security;
